import React from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowLeft } from 'lucide-react';
import { FormLoadingOverlay } from '../common/FormLoadingOverlay';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isLoading?: boolean;
  submitLabel: string;
  photoSection: React.ReactNode;
  children: React.ReactNode;
  darkMode?: boolean;
  embedded?: boolean; // When true, renders without modal overlay for main frame display
}

export const RegistrationModal: React.FC<RegistrationModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onSubmit,
  isLoading = false,
  submitLabel,
  photoSection,
  children,
  darkMode = false,
  embedded = false,
}) => {
  if (!embedded && !isOpen) return null;

  // Modal content component (reused in both modal and embedded modes)
  const modalContent = (
    <div className={`rounded-xl shadow-xl w-full ${
      embedded ? 'h-full' : 'max-w-4xl max-h-[90vh]'
    } flex flex-col ${
      darkMode ? 'bg-gray-950' : 'bg-white'
    }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${
          darkMode ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            {embedded && (
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className={`p-1.5 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50`}
                title="Voltar"
              >
                <ArrowLeft className="h-5 w-5 text-gray-400" />
              </button>
            )}
            <div>
              <h2 className={`text-lg font-semibold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>{title}</h2>
              {subtitle && (
                <p className={`text-sm mt-0.5 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>{subtitle}</p>
              )}
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`p-1.5 text-gray-400 rounded-lg transition-colors disabled:opacity-50 ${
                darkMode ? 'hover:text-gray-300 hover:bg-gray-800' : 'hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body - Two Column Layout */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto custom-scrollbar relative">
          {/* Loading overlay */}
          {isLoading && (
            <FormLoadingOverlay
              isLoading={isLoading}
              message="Salvando..."
              darkMode={darkMode}
            />
          )}

          <div className="flex gap-5 p-5">
            {/* Left Column - Photo */}
            <div className="w-52 flex-shrink-0">
              {photoSection}
            </div>

            {/* Right Column - Form Fields */}
            <div className="flex-1 space-y-4">
              {children}
            </div>
          </div>

          {/* Footer */}
          <div className={`sticky bottom-0 border-t px-5 py-4 flex items-center justify-end gap-3 ${
            darkMode ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-200'
          }`}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode
                  ? 'text-gray-300 bg-gray-800 border border-gray-700 hover:bg-gray-700'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-black hover:bg-gray-800'
              }`}
            >
              {isLoading ? 'Salvando...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
  );

  if (embedded) {
    /* Embedded mode: render content directly without overlay */
    return modalContent as React.ReactElement;
  }

  /* Modal mode: portal to #modal-root (a direct child of the outer h-screen container in
   * Layout.tsx, OUTSIDE the overflow-y-auto main element).  Using position:absolute on a
   * relative ancestor that IS the viewport avoids both problems on Windows/Electron:
   *   1. position:fixed inside overflow-y-auto → SwiftShader paints black (stacking-context bug)
   *   2. position:fixed portaled to document.body → SwiftShader hard-crashes the renderer */
  return ReactDOM.createPortal(
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {modalContent}
    </div>,
    document.getElementById('modal-root') ?? document.body
  );
};

export default RegistrationModal;
